import React, { useState, useEffect, useRef, useMemo } from 'react';
import classnames from 'classnames';
import { Card } from 'tea-component';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import PerformanceSummary from './PerformanceSummary';
import AboutGame from './AboutGame';
import SimilarGames from './SimilarGames';
import Filter from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overview/components/OverviewFilter';
import { connect, useIntl, useDispatch } from 'umi';
import { COMBINEID_REG } from '@intelligence/utils/constant';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import { getMobileDetailIndexRank, getEditionList } from '@/pages/intelligence/services/topcharts';
import {
  createDetailIndexFetcher,
  createDetailIndexRankFetcher,
  createSingleGameDetailFetcher,
} from './services';
import {
  getVideosCommon,
  updateVideosPosterCommon,
  mergeCardRankData,
  transRankParams,
} from '@/pages/intelligence/utils/topchartsUtils';
import { swiperParamsInfo } from '@/pages/intelligence/pages/_config/topchartsInterface';
import OverviewTable from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overview/components/Table';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { buriedPointGame } from '@/pages/home/utils/buriedPoint';
import { toOverviewDateType, toDateTypeLy, toDateTypeNor } from '@/pages/intelligence/utils/common';
import { isEmpty, trankMarketsParamsExceptGlobal, getURLSearchParams } from '@/pages/intelligence/utils/commonUtils';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { getCountryCodeIntersection } from '@intelligence/utils/filterUtils';
import { formatRegionOptions } from '@intelligence/components/RegionSelector';
import { deriveRegionValue } from '@intelligence/v3/utils/region';
import { PC_CONSOLE_COMBINE_ENTITY_TYPE } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import type { IGameType } from '@/pages/intelligence/types/common';
import type { ISensorTowerTag } from '@intelligence/types/topCharts';
import type { IReleaseDateByPlatformRes } from '@intelligence/pages/TopCharts/components/DetailsPage/ReleaseDateByPlatform/types';
import { DETAIL_PAGEID } from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/_config';
import { getIsFortyPage } from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/_utils';
import PublicTrueDataCollection from '@/pages/intelligence/pages/TopCharts/components/PublicTrueDataCollection';
import { appendAdjustedRevenueFilter, REVENUE_SCOPE_VALUES } from '@intelligence/utils/revenueScope';

const PageLogMesMap: {
  [key in IGameType]: any;
} = {
  mobile: dbiPageLogMes.mobileGameDetailsOverview,
  pc: dbiPageLogMes.pcGameDetailsOverview,
  console: dbiPageLogMes.consoleGameDetailsOverview,
  [PC_CONSOLE_COMBINE_ENTITY_TYPE]: dbiPageLogMes.pcConsoleGameDetailsOverview,
};

function Overlay(props: any) {
  const {
    appIdList,
    gameId,
    entityType,
    isSingle,
    basicInfo,
    basicInfoOthers,
    setActiveId,
    activeId,
    moduleName,
    loading: pLoading,
    setHref,
    setActiveSegment,
    detailIndex,
    setDetailIndex,
    unPublishedAppIdList = [],
    inactiveAppIdList = [],
    topchartsState,
    appIdListAll,
    isCalibrated, // 切换tabs时没有使用状态，此时isCalibrated为undefined，去本地里面做判断，所以isCalibrated是变量
    receiveSumFun,
    setAppIdList,
    releaseInFortyDay,
    definitionsLoading,
    genreOptionsMap,
    filterTags,
    gameDetails,
  } = props;

  // const { startUpcomingTopTime, endUpcomingTopTime } = topchartsState;
  // console.log('Overlay', props)

  const getDetailIndex = createDetailIndexFetcher(entityType);
  const getDetailIndexRank = createDetailIndexRankFetcher(entityType);
  const getSingleGameDetail = createSingleGameDetailFetcher(entityType);

  // Extract Game Engine tags from gameDetails (detail_plus API)
  // game_engine from detail_plus API is an array of strings
  const gameEngineTags = useMemo(() => {
    const gameEngineFromDetails = gameDetails?.game_engine;
    if (Array.isArray(gameEngineFromDetails)) {
      return gameEngineFromDetails.filter(Boolean);
    } else if (typeof gameEngineFromDetails === 'string' && gameEngineFromDetails.trim()) {
      return gameEngineFromDetails.trim().split('|');
    }
    return [];
  }, [gameDetails?.game_engine]);

  const [regionOptions, setRegionOptions] = useState([]);
  const [filterLoading, setFilterLoading] = useState(true);
  const { formatMessage } = useIntl(); // 中英切换方法
  const { isOperateDetail, detailFilterExtend, isOperateRegion } = useFilterExtend({});
  // 非继承的情况下，且是pc/combine页，使用is_forty_days===true时，默认为日粒度
  const isFortyDay = getIsFortyPage(DETAIL_PAGEID.overview, entityType, releaseInFortyDay);
  const isFortyDayTimeType = !isOperateDetail && isFortyDay;
  const [conditionDate, setConditionDate] = useState(
    isFortyDayTimeType
      ? 'day'
      : topchartsState.timeType?.toLocaleLowerCase() || toOverviewDateType[entityType],
  );
  const formatRegionParams = (markets: string[]) => {
    // 保存的方案选择后可特殊处理国家值为交集之后的值
    const { countryCode } = formatRegionOptions(regionOptions);
    const result = getCountryCodeIntersection({
      markets,
      countriesMarkets: countryCode,
      disableNoChina: false,
      defaultMarkets: ['global'],
    });
    return result;
  };

  const getExtendParams = () => {
    const newFetchParams: any = {};
    const timeTypeExtend = detailFilterExtend?.[filterKeyMap.TIME_TYPE];
    const marketExtend = detailFilterExtend?.[filterKeyMap.REGION];

    if (marketExtend) {
      // 不同国家国家取交集
      const result = formatRegionParams(marketExtend?.markets);

      // 只有global和globalexeptchina是region,其他发market
      const regionValues = trankMarketsParamsExceptGlobal({
        regionType: marketExtend?.region_type,
        regions: result, // marketExtend?.regions,
        markets: result, // marketExtend?.markets,
      });
      newFetchParams.regionType = regionValues?.regionType || 'region';
      newFetchParams.regions = regionValues?.regions;
    }

    newFetchParams.dateType = timeTypeExtend?.value ?? toOverviewDateType[entityType];
    return {
      dateType: newFetchParams.dateType,
      regionType: newFetchParams?.regionType ?? 'region',
      regions: newFetchParams.regions ?? ['global'],
    };
  };

  const getInitialParams = () => {
    if (isOperateDetail) return getExtendParams();
    return {
      dateType: isFortyDayTimeType ? 'daily' : toOverviewDateType[entityType],
      regionType: 'region',
      regions: ['global'],
    };
  };
  const [initialParams, setInitialParams] = useState(getInitialParams());
  const [initialFilterValue, setInitialFilterValue] = useState({});

  const getUrlDateType = () => {
    const granularity = getURLSearchParams('granularity');
    return granularity && ['daily', 'weekly', 'monthly'].includes(granularity)
      ? toDateTypeNor[granularity]
      : 'day';
  };

  const getUrlRegionMarkets = () => {
    const region = getURLSearchParams('region');
    if (!region) {
      return [];
    }
    return region
      .split(',')
      .map((item) => item?.trim())
      .filter(Boolean);
  };

  const getInitialState = () => {
    const initialParamsTmp = getInitialParams();
    const initialFilterValueTmp: any = {};
    const isMobile = entityType === 'mobile';
    const urlDateType = getUrlDateType();
    const urlRegionMarkets = getUrlRegionMarkets();
    const filterDateType =
      toDateTypeNor[urlDateType || initialParamsTmp.dateType] ||
      urlDateType ||
      initialParamsTmp.dateType;

    if (urlDateType) {
      initialParamsTmp.dateType = urlDateType;
    }

    initialFilterValueTmp[filterKeyMap.TIME_TYPE] = {
      value: filterDateType,
    };
    if (isMobile) {
      initialFilterValueTmp[filterKeyMap.REVENUE_SCOPE] = {
        value: detailFilterExtend?.[filterKeyMap.REVENUE_SCOPE]?.value || REVENUE_SCOPE_VALUES.EXCLUDING,
      };
    }

    if (urlRegionMarkets.length > 0) {
      const regionValue = deriveRegionValue(urlRegionMarkets, regionOptions);
      const regionParams = trankMarketsParamsExceptGlobal({
        regionType: regionValue?.type,
        regions: regionValue?.regions,
        markets: regionValue?.markets,
      });

      initialParamsTmp.regionType = regionParams?.regionType || initialParamsTmp.regionType;
      initialParamsTmp.regions = regionParams?.regions || initialParamsTmp.regions;
      initialFilterValueTmp[filterKeyMap.REGION] = regionValue;
    }

    const initialState: any = {
      initialParams: initialParamsTmp,
      initialFilterValue: initialFilterValueTmp,
    };
    if (isMobile) {
      initialState.revenueScope =
        initialFilterValueTmp?.[filterKeyMap.REVENUE_SCOPE]?.value || REVENUE_SCOPE_VALUES.EXCLUDING;
    }
    return initialState;
  };

  const dispatch = useDispatch();
  // 判断默认显示的数据源
  let sourse = '';
  if (isCalibrated) {
    sourse = 'databrain';
  } else if (isCalibrated === false) {
    sourse = 'appannie';
  } else {
    sourse = localStorage.getItem('initPlatform')
      ? localStorage.getItem('initPlatform') || 'appannie'
      : 'appannie';
  }

  // 把初始的数据源储存到本地做记录
  localStorage.setItem('initPlatform', sourse);

  const [eLoading, setELoading] = useState(false);
  const [cardloading, setCardLoading] = useState(true);
  const [dLoading, setDLoading] = useState(true);
  const [conditionMarket, setConditionMarket] = useState({
    regionType: initialParams?.regionType,
    regions: initialParams?.regions,
  });

  // 暂时固定为'appannie'了
  const [conditionPlatform, setConditionPlatform] = useState('sensortower');
  const timeTypeValueRef = useRef(
    topchartsState.timeType?.toLocaleLowerCase() || toOverviewDateType[entityType],
  );
  const [videoScreen, setVideoScreen] = useState([]);
  const [similarGames, setSimilarGames] = useState([]);

  const [seriesImages, setSeriesImages] = useState([]);
  const [seriesVideos, setSeriesVideos] = useState([]);
  const [seriesTimelines, setSeriesTimelines] = useState([]);
  // const [similarGames, setSimilarGames] = useState([]);
  const [seriesDesc, setSeriesDesc] = useState('');
  const [tableList, setTableList] = useState([]);
  const [downloadContent, setDownloadContent] = useState<{ [key: string]: any[] }>({});
  const [releaseDateByPlatforms, setReleaseDateByPlatforms] = useState<IReleaseDateByPlatformRes[]>(
    [],
  );
  const [sensorTowerTags, setSensorTowerTags] = useState<ISensorTowerTag[]>([]);
  const [rankLoading, setRankLoading] = useState(true);

  const getIndex = async (
    conditionMarket: any,
    conditionDate: any,
    revenueScope: string,
    isChangeRegion = isOperateRegion,
  ) => {
    setCardLoading(true);
    const pcconsoleParamObj = {
      unified_id: gameId,
      entity_type: entityType,
      data_sources: [],
      date_type: conditionDate == '' ? toOverviewDateType[entityType] : conditionDate,
      is_first: topchartsState.isOverviewFirst,
      region_type: conditionMarket.regionType,
      regions: conditionMarket.regions,
      is_change_region: entityType == 'mobile' ? true : isChangeRegion,
      is_new_sytle: true,
    };
    const mobileParamObj = {
      ...pcconsoleParamObj,
      app_ids: appIdList,
      data_sources: [conditionPlatform],
      id: gameId,
      date_type: toDateTypeLy[pcconsoleParamObj.date_type],
    };
    const paramObj = entityType === 'mobile'
      ? appendAdjustedRevenueFilter(mobileParamObj, revenueScope)
      : pcconsoleParamObj;
    timeTypeValueRef.current = conditionDate;
    // 添加融合ID校验规则
    if (COMBINEID_REG.test(gameId)) {
      try {
        const cardRes = await getDetailIndex(paramObj);
        if (!isEmpty(cardRes)) {
          setDetailIndex(cardRes?.data);
          props.dispatch({
            type: 'dbiTopcharts/setTimeTypeOverview',
            payload: cardRes?.req?.date_type || conditionDate,
          });
          props.dispatch({
            type: 'dbiTopcharts/setRegionTypeOverview',
            payload: conditionMarket.regionType || 'region',
          });
          props.dispatch({
            type: 'dbiTopcharts/setRegionsOverview',
            payload: conditionMarket.regions || ['global'],
          });
          props.dispatch({ type: 'dbiTopcharts/setIsOverviewFirst', payload: false });
          setConditionDate(cardRes?.req?.date_type || conditionDate);
          timeTypeValueRef.current = cardRes?.req?.date_type || conditionDate;
          return {
            res: cardRes,
            paramObj,
          };
        }
        return {
          res: {},
          paramObj,
        };
      } catch (err) {
        console.log(err);
        setConditionDate(conditionDate);
        return {
          res: {},
          paramObj,
        };
      } finally {
        setCardLoading(false);
      }
    } else {
      setConditionDate(conditionDate);
      setCardLoading(false);
      return {
        res: {},
        paramObj,
      };
    }
  };

  const getRankData = async (_list: any, rankApiData: any, revenueScope: string) => {
    setRankLoading(true);
    // 添加游戏ID校验规则
    if (COMBINEID_REG.test(rankApiData?.unified_id)) {
      try {
        const rankApiDataTemp = {
          ...rankApiData,
          is_change_region: true,
        };

        const resRank = await getMobileDetailIndexRank(
          appendAdjustedRevenueFilter(rankApiDataTemp, revenueScope),
        );
        const newList = mergeCardRankData(_list, resRank?.data);
        /* 合并卡片和Rank内容后回填到卡片*/
        setDetailIndex([...newList]);
      } catch (err) {}
    } else {
    }
    setRankLoading(false);
  };

  const getPCConsoleRankData = async (
    list: any[],
    params: any,
    isChangeRegion = isOperateRegion,
  ) => {
    // 做接口参数兜底，防止报错
    const regions = params?.regions;
    const ranks = params?.rank_date_map;
    // 校验入参中regions不为空
    if (!Array.isArray(regions) || !regions?.length) return;
    // 校验入参中rank_date_map不为空
    if (!Array.isArray(ranks) || !ranks?.length) return;
    setRankLoading(true);
    try {
      const paramsTemp = {
        ...params,
        is_change_region: entityType == 'mobile' ? true : isChangeRegion,
        is_new_sytle: true,
      };
      const resRank = await getDetailIndexRank(paramsTemp);
      const newList = mergeCardRankData(list, resRank?.data);
      /* 合并卡片和Rank内容后回填到卡片 */
      setDetailIndex([...newList]);
    } finally {
      setRankLoading(false);
    }
  };

  const getRankDataByEntityType = (
    list: any[],
    mobileParams: any,
    pcConsoleParams: any,
    revenueScope: string,
    isChangeRegion = isOperateRegion,
  ) => {
    if (entityType === 'mobile') {
      getRankData(list, mobileParams, revenueScope);
    } else {
      // PC、Console 同一个接口
      getPCConsoleRankData(list, pcConsoleParams, isChangeRegion);
    }
  };

  const fetchEditionList = async (
    conditionMarket: any,
    conditionDate: any,
    summaryRes: any,
    rank_date_map: any,
    revenueScope: string,
  ) => {
    setELoading(true);

    const editionParsms = {
      app_id: summaryRes?.req?.app_ids,
      period: toDateTypeLy[conditionDate],
      region_type: conditionMarket.regionType,
      regions: conditionMarket.regions,
      order_metric: 'release',
      order: 'desc',
      metric_date: rank_date_map,
    };
    /**
     * 接口速率优化：从summary的卡片列表获取指标时间带到参数里
     */
    // const summaryList = summaryRes?.data;
    // const metric_date = transEditionParams(summaryList, summaryRes?.req?.date_type);
    // const editionApiData = {
    //   ...editionParsms,
    //   metric_date: summaryRes?.req?.rank_date_map,
    // }
    try {
      const editionRes = await getEditionList(appendAdjustedRevenueFilter(editionParsms, revenueScope));
      setTableList(editionRes?.data || []);
    } catch (err) {}
    setELoading(false);
  };

  const getImageList = (images: any) => (images != '' ? images?.split('|') : []);

  const swiperParams: swiperParamsInfo = {
    showArrows: true,
    showIndicators: false,
    infiniteLoop: true,
    autoPlay: false,
    emulateTouch: true,
    showStatus: false,
  };

  const allAppIds = () => {
    if (appIdListAll?.length != 0) {
      return appIdListAll?.map((item: { id: any }) => item.id);
    }
    return [];
  };

  // 获取游戏系列Similar Games的数据
  const getSeriesGamesData = async (conditionMarketTpm: any, id = '') => {
    setDLoading(true);
    const result = await getSingleGameDetail({
      unified_id: gameId,
      app_id: id,
      app_ids: allAppIds(),
      entity_type: entityType,
      region_type: conditionMarketTpm.regionType,
      regions: conditionMarketTpm.regions,
      basic_info_others: basicInfoOthers,
    });
    const seriesVideos = getVideosCommon(result?.data?.video);
    updateVideosPosterCommon(seriesVideos, setSeriesVideos);
    setSeriesVideos(seriesVideos);
    setSeriesImages(getImageList(result?.data?.images || ''));
    // 获取视频截图
    const screenList = result?.data?.video_screen?.split('|');
    setVideoScreen(screenList || []);
    setSeriesTimelines(result?.data?.timelines || []);
    setSimilarGames(result?.data?.similar_games || []);
    const downloadableContents = result?.data?.download_content || {};
    const releaseDateByPlatforms = result?.data?.release_dates_by_platform || [];
    const sensorTowerTags = result?.data?.sensortower_tag || [];
    setDownloadContent(downloadableContents);
    setReleaseDateByPlatforms(releaseDateByPlatforms);
    setSensorTowerTags(sensorTowerTags);
    setSeriesDesc(result?.data?.description || '');
    setDLoading(false);
  };

  const getData = async (
    conditionMarketTpm: any,
    conditionDateTpm: any,
    revenueScope: string,
    isChangeRegion = isOperateRegion,
  ) => {
    if (topchartsState.appIdList?.length > 0) {
      setELoading(true); // 因为fetchEditionList需要等参数，所以它应该提前加Loading
    }

    const { res, paramObj } = await getIndex(
      conditionMarketTpm,
      conditionDateTpm,
      revenueScope,
      isChangeRegion,
    );

    const _list = res?.data;
    const rank_date_map = transRankParams(_list, res?.req?.date_type);
    const rankApiData = {
      ...paramObj,
      rank_date_map,
    };
    getRankDataByEntityType(
      _list,
      rankApiData,
      rankApiData,
      revenueScope,
      isChangeRegion,
    );

    if (isSingle) {
      // 如果是直接进入单游戏详情页，要传游戏id过来
      getSeriesGamesData(
        conditionMarketTpm,
        Array.isArray(appIdList) && appIdList?.length > 0 ? appIdList[0] : '',
      );
      setELoading(false);
    } else {
      const rankDataMapEdition = transRankParams(_list, res?.req?.date_type, true);

      if (!isSingle && res?.req?.app_ids?.length > 0) {
        fetchEditionList(conditionMarketTpm, conditionDateTpm, res, rankDataMapEdition, revenueScope);
      } else {
        setTableList([]);
        setELoading(false);
      }
      getSeriesGamesData(conditionMarketTpm);
    }
  };

  useEffect(() => {
    if (pLoading || filterLoading) {
      setCardLoading(true);
      setDLoading(true);
      setELoading(true);
    } else {
      // 如果是从首页搜索进来，第一次baseinfo调用完，还没有获取卡片数据，就调用一次
      const { initialParams, initialFilterValue, revenueScope } = getInitialState();
      const { dateType, regionType, regions } = initialParams ?? {};

      setConditionDate(dateType);
      timeTypeValueRef.current = dateType;
      setConditionMarket({
        regionType,
        regions,
      });
      setInitialParams(initialParams);
      setInitialFilterValue(initialFilterValue);

      getData({ regionType, regions }, dateType, revenueScope);
      handlePageLog(gameId);
    }
  }, [pLoading, filterLoading]);

  const handlePageLog = (gameId: string) => {
    const pageLogMes = PageLogMesMap[entityType] || dbiPageLogMes.mobileGameDetailsOverview;
    if (gameId) {
      buriedPointGame(pageLogMes, gameId);
    }
    // 每次打点的时候，记录当前打点信息用于详情页头部的feedback参数
    recordPageMesForFeedback(pageLogMes);
  };

  const recordPageMesForFeedback = (logMes: any) => {
    dispatch({
      type: `${DBI_TOPCHARTS}/setActivedPageLogMes`,
      payload: logMes,
    });
  };

  const handleConfirm = (value: any, nextInitialFilterValue: any) => {
    setCardLoading(true);
    setDLoading(true);
    setELoading(true);
    let revenueScope = '';
    if (entityType === 'mobile') {
      revenueScope = value.revenue_scope || REVENUE_SCOPE_VALUES.EXCLUDING;
    }

    if (nextInitialFilterValue) {
      setInitialFilterValue(nextInitialFilterValue);
    }

    setConditionMarket({
      regionType: value.region_type,
      regions: value.regions,
    });
    // 如果时间粒度参数相同，则是操作修改了region
    const isChangeRegionTemp = value.date_type === timeTypeValueRef.current;
    getData(
      { regionType: value.region_type, regions: value.regions },
      value.date_type,
      revenueScope,
      isChangeRegionTemp,
    );
  };

  const onFilterInit = (params: any) => {
    const { loading, regionOptions } = params || {};
    setFilterLoading(loading);
    setRegionOptions(regionOptions);
  };

  return (
    <div className={classnames('mobile-games-overly', { 'mobile-games-overly--mobile': entityType === 'mobile' })}>
      {/* Public True Data区域 */}
      <PublicTrueDataCollection
        {...props}
        gameId={gameId}
        publicTrueDataReportBtnLogKey="gameDetailsOverviewTrueAllDataReport"
      />

      <div className="overly-filter">
        <Filter
          buttonLogConfig={{}}
          filterConfirm={handleConfirm}
          defaultDate={conditionDate}
          initialValue={initialFilterValue}
          onFilterInit={onFilterInit}
          entityType={entityType}
        />
      </div>

      <PerformanceSummary
        isCalibrated={isCalibrated}
        moduleName={moduleName}
        gameId={gameId}
        detailIndex={detailIndex}
        conditionPlatform={conditionPlatform}
        conditionDate={conditionDate}
        setActiveId={setActiveId}
        setHref={setHref}
        conditionMarket={conditionMarket}
        entityType={entityType}
        rankLoading={rankLoading}
        cardloading={cardloading}
        activeId={activeId}
        setActiveSegment={setActiveSegment}
        isOperateRegion={isOperateRegion}
      />
      {!isSingle && (
        <Card>
          <Card.Body
            title={
              <div className="performance-felx">
                <span className="performance-felx-text">
                  {formatMessage({ id: `dbi_editions_list` })}
                </span>
                <TipContext tipText={formatMessage({ id: 'dbi_edition_list_tip' })} />
              </div>
            }
            // operation={
            //   <div className="performance-felx-desc">
            //     2023-01-01 &nbsp;to &nbsp;2023-01-02
            //   </div>
            // }
          >
            {/* <OtherTable tableList={tableList} /> */}
            <OverviewTable
              gameId={gameId}
              tableList={tableList}
              unPublishedAppIdList={unPublishedAppIdList}
              inactiveAppIdList={inactiveAppIdList}
              eLoading={eLoading}
              receiveSumFun={receiveSumFun}
              setAppIdList={setAppIdList}
              dateType={conditionDate}
              dataSource={conditionPlatform}
            />
          </Card.Body>
        </Card>
      )}
      <AboutGame
        isRelease={basicInfo?.is_release}
        images={seriesImages}
        videos={seriesVideos}
        swiperParams={swiperParams}
        desc={seriesDesc}
        timelines={seriesTimelines}
        basicInfo={basicInfo}
        // 以下是解决样式冲突
        leftStyle={{ margin: '20px' }}
        rightStyle={{ marginRight: '20px', marginTop: '20px' }}
        videoScreen={videoScreen}
        dLoading={dLoading}
        entityType={entityType}
        downloadContent={downloadContent}
        releaseDateByPlatforms={releaseDateByPlatforms}
        sensorTowerTags={sensorTowerTags}
        definitionsLoading={definitionsLoading}
        genreOptionsMap={genreOptionsMap}
        filterTags={filterTags}
        gameDetails={gameDetails}
        mainGenre={basicInfo?.genre}
        subGenre={basicInfo?.sub_genre}
        ieggMainGenre={gameDetails?.iegg_genre}
        ieggSubGenre={gameDetails?.iegg_sub_genre}
        gameEngineTags={gameEngineTags}
      />
      <SimilarGames
        isRelease={basicInfo?.is_release}
        similarGames={similarGames}
        activeId={activeId}
        moduleName={moduleName}
        entityType={entityType}
        setDetailIndex={setDetailIndex}
        dLoading={dLoading}
        gameId={gameId}
      />
    </div>
  );
}

export default connect((state: any) => ({
  filterState: state[FILTER_EXTEND_NAMESPACE],
  topchartsState: state[DBI_TOPCHARTS],
}))(Overlay);
